// VFX 시스템(vfx-library v1 system.ts 이식): 텍스처별 스프라이트 묶음, 지연 생성 큐, 불똥 요청 전달.
// 라이브러리와 다른 점: 스프라이트를 텍스처별 InstancedMesh로 묶는다(드로우콜 = 쓰는 텍스처 수), 불똥은 게임 ParticleFx(드로우콜 1)로 보낸다,
// 섬광·틴트는 없다(전체 화면 섬광은 각성만, FlashLimiter·PostFx 경유 — COMBAT-FEEL 5장). 생성 뒤 프레임 루프에서 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { SpriteBatch } from './batch';
import { BATCH_CAP, SPEC, type SpriteSpec } from './specs';
import { TextureBank, type TexId } from './textures';

/** 불똥 요청(ParticleFx.streaks 인자). 색은 HDR(1 넘어도 됨), white = 흰 코어 비율 */
export interface Burst { x: number; y: number; z: number; dir: number; spread: number; count: number; speed: number; r: number; g: number; b: number; white: number; life: number; up: number }

const JOB_SPRITE = 0, JOB_BURST = 1;
interface Deferred {
  at: number; kind: number; used: boolean; spec: SpriteSpec | null;
  x: number; y: number; z: number; f: number; s: number; r: number; flip: boolean; ka: number;
  burst: Burst;
}
const QUEUE = 160;

export const newBurst = (): Burst => ({ x: 0, y: 0, z: 0, dir: 0, spread: 0, count: 0, speed: 0, r: 0, g: 0, b: 0, white: 0, life: 0, up: 0 });

export class VfxSystem {
  readonly group = new THREE.Group();
  readonly bank = new TextureBank();
  private readonly bySpec = new Map<SpriteSpec, SpriteBatch>();
  private readonly batches: SpriteBatch[] = [];
  private readonly texOf: TexId[] = [];
  private readonly queue: Deferred[] = [];
  /** 불똥 요청을 받는 쪽(CombatFx가 ParticleFx로 잇는다) */
  onBurst: ((b: Burst) => void) | null = null;
  private time = 0;
  private seed = 1;
  /** 풀이 가득 차 버린 스프라이트 수(QA) */
  dropped = 0;

  constructor() {
    this.group.name = 'vfx-lib';
    const byTex = new Map<string, SpriteBatch>();
    for (const spec of Object.values(SPEC) as SpriteSpec[]) {
      const key = spec.tex + '|' + spec.blend;
      let b = byTex.get(key);
      if (!b) {
        b = new SpriteBatch(BATCH_CAP[spec.tex] ?? 16, spec.blend, this.bank.blank);
        b.mesh.name = 'vfx-' + spec.tex;
        byTex.set(key, b);
        this.batches.push(b);
        this.texOf.push(spec.tex);
        this.group.add(b.mesh);
      }
      this.bySpec.set(spec, b);
    }
    for (let i = 0; i < QUEUE; i++) {
      this.queue.push({ at: 0, kind: JOB_SPRITE, used: false, spec: null, x: 0, y: 0, z: 0, f: 0, s: 1, r: 0, flip: false, ka: 1, burst: newBurst() });
    }
  }

  /** 쓰는 텍스처만 읽어 묶음에 끼운다. extra: 묶음 밖에서 쓰는 텍스처(영웅을 따라다니는 광주·고리). */
  async load(baseUrl: string, extra: readonly TexId[] = []): Promise<void> {
    const ids: TexId[] = [];
    for (const t of this.texOf) if (!ids.includes(t)) ids.push(t);
    for (const t of extra) if (!ids.includes(t)) ids.push(t);
    await this.bank.load(baseUrl, ids);
    for (let i = 0; i < this.batches.length; i++) {
      const t = this.bank.get(this.texOf[i]);
      if (t) this.batches[i].setTexture(t);
    }
  }

  /** 0..1 결정적 난수(회전 흔들기용) */
  rand(): number { this.seed = (this.seed * 16807) % 2147483647; return this.seed / 2147483647; }

  /** 남은 자리(묶음이 가득 찼는지 미리 보고 덜 중요한 효과를 건너뛸 때) */
  free(spec: SpriteSpec): number { return this.bySpec.get(spec)!.free; }

  /** stretch: 가로 배율(세로 크기 대비, 기본 1). 선분·길쭉한 흔적을 한 장으로 늘일 때 */
  sprite(spec: SpriteSpec, x: number, y: number, z: number, facing = 0, scale = 1, roll = 0, flip = false, stretch = 1): boolean {
    const ok = this.bySpec.get(spec)!.spawn(spec, x, y, z, facing, scale, roll, flip, stretch);
    if (!ok) this.dropped++;
    return ok;
  }

  private slot(delay: number, kind: number): Deferred | null {
    for (let i = 0; i < this.queue.length; i++) {
      const d = this.queue[i];
      if (!d.used) { d.used = true; d.at = this.time + delay; d.kind = kind; return d; }
    }
    this.dropped++;
    return null;
  }

  later(delay: number, spec: SpriteSpec, x: number, y: number, z: number, facing = 0, scale = 1, roll = 0, flip = false, stretch = 1): void {
    if (delay <= 0) { this.sprite(spec, x, y, z, facing, scale, roll, flip, stretch); return; }
    const d = this.slot(delay, JOB_SPRITE); if (!d) return;
    d.spec = spec; d.x = x; d.y = y; d.z = z; d.f = facing; d.s = scale; d.r = roll; d.flip = flip; d.ka = stretch;
  }

  burst(b: Burst): void { if (this.onBurst && b.count > 0) this.onBurst(b); }

  laterBurst(delay: number, b: Burst): void {
    if (delay <= 0) { this.burst(b); return; }
    const d = this.slot(delay, JOB_BURST); if (!d) return;
    Object.assign(d.burst, b);
  }

  /** dt: 초(영웅 시계, 히트스톱·일시정지에 0) */
  update(dt: number, camera: THREE.PerspectiveCamera): void {
    this.time += dt;
    for (let i = 0; i < this.queue.length; i++) {
      const d = this.queue[i];
      if (!d.used || d.at > this.time + 1e-6) continue;
      d.used = false;
      if (d.kind === JOB_SPRITE && d.spec) this.sprite(d.spec, d.x, d.y, d.z, d.f, d.s, d.r, d.flip, d.ka);
      else if (d.kind === JOB_BURST) this.burst(d.burst);
    }
    for (let i = 0; i < this.batches.length; i++) this.batches[i].update(dt, camera);
  }

  prewarm(on: boolean): void { for (const b of this.batches) b.prewarm(on); }

  /** 한 종류 스프라이트만 지운다(성인 모드를 끄면 피 자국을 바로 없앤다). 같은 묶음을 쓰는 다른 종류도 함께 지워지므로 전용 묶음에만 쓴다. 대기 중인 같은 종류도 취소한다. */
  clearSpec(spec: SpriteSpec): void {
    this.bySpec.get(spec)!.clear();
    for (const d of this.queue) if (d.used && d.kind === JOB_SPRITE && d.spec === spec) d.used = false;
  }

  reset(): void {
    this.time = 0; this.seed = 1;
    for (const b of this.batches) b.clear();
    for (const d of this.queue) d.used = false;
  }

  /** 살아 있는 스프라이트 수, 그리는 묶음 수(= 이 시스템의 드로우콜), 지연 대기 수 */
  counts(out: { sprites: number; batches: number; queued: number }): void {
    let s = 0, b = 0, q = 0;
    for (const x of this.batches) { s += x.n; if (x.n > 0) b++; }
    for (const d of this.queue) if (d.used) q++;
    out.sprites = s; out.batches = b; out.queued = q;
  }

  dispose(): void {
    for (const b of this.batches) b.dispose();
    this.batches.length = 0;
    this.bank.dispose();
  }
}

